import React, { useMemo } from 'react';
import MatchCard from '../matches/MatchCard';
import { Spinner, Toaster, Pagination } from '../common';
import { useUpdateMatchStatus } from '../../hooks/useMatch';
import { MATCH_STATUS, type Match } from '../../types/match';
import { getCurrentUsersCount } from '../../utils';
import toast from 'react-hot-toast';
import matchingAnimation from '../../assets/animations/matching-animation.gif';

interface MatchListingProps {
  matches: Match[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  limit: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  variant?: 'horizontal' | 'vertical';
  allowPagination?: boolean;
  wrapperClassName?: string;
  emptyMessage?: string;
  isFindingMatches?: boolean; // REC-487: Show "Finding matches..." state
}

const MatchListing = ({
  matches,
  currentPage,
  totalPages,
  totalItems,
  limit,
  onPageChange,
  loading = false,
  variant,
  allowPagination = true,
  wrapperClassName,
  emptyMessage = 'Try adjusting your filters or check back later',
  isFindingMatches = false,
}: MatchListingProps) => {
  const { mutateAsync: updateMatchStatus } = useUpdateMatchStatus();

  // Apr-18: useMemo MUST be declared before any conditional
  // early return below. Previously sat after `if (loading)` and
  // `if (matches.length === 0)` early returns, which violated the Rules
  // of Hooks — when matches went from empty to populated, React saw a
  // different hook count between renders and threw error #310. Moving
  // the hook up to the top-of-component fixes the crash on /matches and
  // dashboard when the first batch of matches lands.
  //
  // `matches` can arrive as `undefined` from parents that pass
  // `matches?.result.items` (e.g. PendingMatches' filteredMatches while
  // the query is loading) — default to [] inside the memo to avoid a
  // TypeError on .filter(...) during initial render.
  const { primaryMatches, adjacentMatches, showSectionSplit } = useMemo(() => {
    const safe = matches ?? [];
    const primary = safe.filter(m => m.reciprocal === true);
    const adjacent = safe.filter(m => m.reciprocal === false);
    const unknown = safe.filter(m => m.reciprocal == null);
    // Only split when the backend actually gave us at least one true/false
    // signal. If every match is null (pre-feature matches), keep the flat list.
    const hasSignal = primary.length + adjacent.length > 0;
    return {
      primaryMatches: primary,
      // Unknown reciprocity lands in Adjacent (conservative — not a confirmed
      // direct exchange). This avoids a third "Other" section and keeps the
      // UX simple.
      adjacentMatches: [...adjacent, ...unknown],
      showSectionSplit: hasSignal,
    };
  }, [matches]);

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const updateMatchStatusMutation = async (id: string, status: string) => {
    let flag = false;
    await updateMatchStatus(
      { match_id: id, status },
      {
        onSuccess: () => {
          onPageChange(currentPage);
          flag = true;
        },
        onError: error => {
          toast.custom(t => (
            <Toaster message={error?.message} type='error' id={t.id} />
          ));
          flag = false;
        },
      }
    );
    return flag;
  };

  const onApprove = async (match: Match) => {
    const success = await updateMatchStatusMutation(
      match.id,
      MATCH_STATUS.APPROVED
    );
    if (!success) {
      return;
    }

    toast.custom(t => (
      <Toaster
        message={`You have approved ${match.other_user.name}!`}
        type='success'
        id={t.id}
      />
    ));
  };

  const onReject = async (match: Match, open: boolean) => {
    if (open) {
      return;
    }

    const success = await updateMatchStatusMutation(
      match.id,
      MATCH_STATUS.REJECTED
    );
    if (!success) {
      return;
    }

    toast.custom(t => (
      <Toaster
        message={`You have passed ${match.other_user.name}!`}
        type='success'
        id={t.id}
      />
    ));
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12 h-calc(100dvh-14rem)'>
        <Spinner size='md' />
      </div>
    );
  }

  if ((matches ?? []).length === 0) {
    // REC-487: Show "Finding matches..." when background processing is in progress
    // Apr-27: replaced spinner with designer-delivered E-1 logo animation
    // (500KB GIF). Brand-consistent loading state — the animated 2C logo signals
    // "system is working on YOUR connections" rather than the generic spinner.
    if (isFindingMatches) {
      return (
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <img
              src={matchingAnimation}
              alt='Finding your matches'
              className='w-32 h-32 mx-auto'
            />
            <div className='text-primary-500 mt-4 mb-2 font-medium'>
              Finding your matches...
            </div>
            <div className='text-sm text-body-300'>
              Our AI is analyzing profiles to find the best connections for you.
              <br />
              This usually takes less than a minute.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center max-w-sm'>
          <div className='text-body-400 mb-2 font-medium'>No matches yet</div>
          <div className='text-sm text-body-300 leading-relaxed'>
            {/* Apr-27: empty-state copy rewritten per research-backed
                3-part framework (educative + CTA + brand-warm). Old copy
                ("You're among the first to complete your profile!") was
                misleading — the platform has 270+ users; what's actually
                happening is Hard Rule 6 enforcing strict criteria. New copy
                reframes 0-matches as a feature ("we're being selective")
                and gives a single actionable CTA. Mobile mirror in
                2connect-mobile-app/src/app/(tabs)/{index,matches}.tsx. */}
            {emptyMessage || 'We only surface connections that genuinely fit your criteria. Check back as new profiles arrive, or refine your preferences in Profile to broaden the search.'}
          </div>
          {!emptyMessage && (
            <button
              type='button'
              onClick={() => { window.location.href = '/profile'; }}
              className='mt-4 inline-flex items-center px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors'
            >
              Refine preferences
            </button>
          )}
        </div>
      </div>
    );
  }

  // Reciprocity partition moved to top of component — see comment on the
  // useMemo declaration above for the Rules-of-Hooks fix.

  const renderMatchGrid = (list: Match[]) => (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-4 ${wrapperClassName ?? ''}`}
    >
      {list.map(match => (
        <MatchCard
          key={match.id}
          match={match}
          variant={variant}
          onApprove={onApprove ? () => onApprove(match) : undefined}
          onReject={onReject ? open => onReject(match, open) : undefined}
        />
      ))}
    </div>
  );

  return (
    <div>
      {/* Jun-19 (a user V1 test): the dashboard (horizontal variant) wraps this
          list in its own scroll container (HomePage max-h-[35rem] overflow-y-auto),
          so this inner overflow produced a SECOND scrollbar. Only self-scroll on
          the full Matches page (vertical); let the parent scroll on the dashboard. */}
      <div className={`mb-6 ${variant === 'horizontal' ? '' : 'max-h-[calc(100dvh-14rem)] overflow-y-auto'}`}>
        {showSectionSplit ? (
          <>
            {primaryMatches.length > 0 && (
              <section className='mb-6'>
                <div className='flex items-baseline justify-between mb-3'>
                  <h6 className='!font-medium text-heading-500 flex items-center gap-2'>
                    <span className='text-green-600' aria-hidden>⊕</span>
                    Primary Matches
                    <span className='text-sm text-body-400 font-normal'>
                      ({primaryMatches.length})
                    </span>
                  </h6>
                  {/* Jun-17 (a user round 2, R2-3): "what is this and why is it
                      here?" — replaced the opaque "Direct value exchange" line
                      with plain English. */}
                  <p className='text-xs text-body-300 font-medium hidden sm:block'>
                    People whose goals line up directly with yours
                  </p>
                </div>
                {renderMatchGrid(primaryMatches)}
              </section>
            )}
            {adjacentMatches.length > 0 && (
              <section>
                <div className='flex items-baseline justify-between mb-3'>
                  <h6 className='!font-medium text-heading-500 flex items-center gap-2'>
                    <span className='text-body-400' aria-hidden>○</span>
                    Adjacent Matches
                    <span className='text-sm text-body-400 font-normal'>
                      ({adjacentMatches.length})
                    </span>
                  </h6>
                  <p className='text-xs text-body-300 font-medium hidden sm:block'>
                    Related profiles worth a look beyond the exact match
                  </p>
                </div>
                {renderMatchGrid(adjacentMatches)}
              </section>
            )}
          </>
        ) : (
          renderMatchGrid(matches ?? [])
        )}
      </div>

      {totalPages > 1 && allowPagination && (
        <div className='flex items-center justify-between'>
          <div className='text-sm text-light-gray-500 font-medium'>
            Displaying{' '}
            <span className='text-theme-blue-700'>
              {getCurrentUsersCount(currentPage, limit, totalItems)}
            </span>{' '}
            out of <span className='text-theme-blue-700'>{totalItems}</span>
          </div>
          <div className='flex justify-center'>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={onPageChange}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchListing;
